<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { Nutzungsart, NutzungsartCollection } from '@/models/Nutzungsart';
import Checkbox from 'primevue/checkbox';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'reihenfolge' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new NutzungsartCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: Nutzungsart) {
    router.push({ name: 'bg_allg_nutzungsart_edit', params: { id: r?.id } });
}

async function deleteRecord(r: Nutzungsart) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_allg_nutzungsart_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('nutzungsarten')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as Nutzungsart)"
            @deleterecord="deleteRecord($event as Nutzungsart)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="bezeichnung" sortable :header="$t('bezeichnung')"></Column>
            <Column field="auswertung_alle" :header="$t('auswertung_alle')" bodyStyle="text-align:center">
                <template #body="slotProps">
                    <Checkbox v-model="slotProps.data['auswertung_alle']" binary readonly />
                </template>
            </Column>
            <Column
                field="auswertung_wohn_und_dienstleistungsgebaeude"
                :header="$t('auswertung_wohn_und_dienstleistungsgebaeude')"
                bodyStyle="text-align:center"
            >
                <template #body="slotProps">
                    <Checkbox v-model="slotProps.data['auswertung_wohn_und_dienstleistungsgebaeude']" binary readonly />
                </template>
            </Column>
            <Column
                field="auswertung_wohngebaeude"
                :header="$t('auswertung_wohngebaeude')"
                bodyStyle="text-align:center"
            >
                <template #body="slotProps">
                    <Checkbox v-model="slotProps.data['auswertung_wohngebaeude']" binary readonly />
                </template>
            </Column>
            <Column field="reihenfolge" sortable :header="$t('reihenfolge')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
